import Phaser from 'phaser';
import { COLORS, FONTS, GAME_WIDTH, fontStr } from '@/config';
import { gameState } from '@/state/store';
import type { GameStateData } from '@/state/GameState';
import { SESONG_NAVN, aaret, sesongForDag } from '@/systems/sesong';
import { audioManager } from '@/systems/audioManager';
import { autoSpeed } from '@/systems/autoSpeed';
import { sfx } from '@/systems/sfx';

const HUD_HEIGHT = 156;

export class HUDScene extends Phaser.Scene {
  private kronerText?: Phaser.GameObjects.Text;
  private tierText?: Phaser.GameObjects.Text;
  private dagText?: Phaser.GameObjects.Text;
  private sesongText?: Phaser.GameObjects.Text;
  private omdommeText?: Phaser.GameObjects.Text;
  private unsubscribe?: () => void;

  constructor() {
    super('HUD');
  }

  create(): void {
    this.add
      .rectangle(0, 0, GAME_WIDTH, HUD_HEIGHT, COLORS.white)
      .setOrigin(0, 0)
      .setAlpha(0.98);

    this.add
      .rectangle(0, HUD_HEIGHT, GAME_WIDTH, 4, COLORS.wine)
      .setOrigin(0, 0)
      .setAlpha(0.9);

    // Kroner og Sesong er bredest. Tier (én bokstav) og Dag (1-3 sifre)
    // får små kolonner i midten. Omdømme får en bred kolonne på høyre
    // side så «Anerkjent» og «Etablert» får plass.
    const col1 = 28;
    const col2 = GAME_WIDTH * 0.32;
    const col3 = GAME_WIDTH * 0.43;
    const col4 = GAME_WIDTH * 0.53;
    const col5 = GAME_WIDTH * 0.78;

    this.buildStat(
      col1,
      'Kroner',
      (s) => this.formatKroner(s.kroner),
      (t) => (this.kronerText = t),
      40,
    );
    this.buildStat(col2, 'Tier', (s) => s.tier, (t) => (this.tierText = t), 40);
    this.buildStat(col3, 'Dag', (s) => String(s.dag), (t) => (this.dagText = t), 40);
    this.buildStat(
      col4,
      'Sesong',
      (s) => sesongLabel(s.dag),
      (t) => (this.sesongText = t),
      32,
    );
    this.buildStat(
      col5,
      'Omdømme',
      (s) => omdommeNivaa(s.omdomme),
      (t) => (this.omdommeText = t),
      40,
    );

    this.buildMuteKnapp();
    this.buildSpeedKnapp();

    this.unsubscribe = gameState.subscribe((snap) => this.refresh(snap));

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unsubscribe?.());
  }

  private buildMuteKnapp(): void {
    const x = GAME_WIDTH - 16;
    const y = HUD_HEIGHT - 8;
    const knapp = this.add
      .text(x, y, this.muteSymbol(), {
        fontFamily: FONTS.display,
        fontSize: fontStr(28),
        color: '#8B5E3C',
        backgroundColor: 'rgba(244,234,213,0.85)',
        padding: { left: 8, right: 8, top: 4, bottom: 4 },
      })
      .setOrigin(1, 1)
      .setInteractive({ useHandCursor: true });
    knapp.on('pointerup', () => {
      audioManager.toggleMute();
      knapp.setText(this.muteSymbol());
    });
  }

  private buildSpeedKnapp(): void {
    const x = GAME_WIDTH - 88;
    const y = HUD_HEIGHT - 8;
    const knapp = this.add
      .text(x, y, autoSpeed.symbol(), {
        fontFamily: FONTS.display,
        fontSize: fontStr(28),
        color: '#8B5E3C',
        backgroundColor: 'rgba(244,234,213,0.85)',
        padding: { left: 8, right: 8, top: 4, bottom: 4 },
      })
      .setOrigin(1, 1)
      .setInteractive({ useHandCursor: true });
    knapp.on('pointerup', () => {
      autoSpeed.cycle();
      knapp.setText(autoSpeed.symbol());
      sfx.play('click');
    });
    const unsub = autoSpeed.subscribe(() => knapp.setText(autoSpeed.symbol()));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, unsub);
  }

  private muteSymbol(): string {
    return audioManager.erAv ? '🔇' : '🔊';
  }

  private buildStat(
    x: number,
    label: string,
    valueFn: (s: GameStateData) => string,
    capture: (t: Phaser.GameObjects.Text) => void,
    fontSize = 40,
  ): void {
    this.add
      .text(x, 22, label.toUpperCase(), {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8B5E3C',
        fontStyle: '600',
      })
      .setOrigin(0, 0);

    const value = this.add
      .text(x, 52, valueFn(gameState.snapshot), {
        fontFamily: FONTS.display,
        fontSize: fontStr(fontSize),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);

    capture(value);
  }

  private refresh(snap: GameStateData): void {
    this.kronerText?.setText(this.formatKroner(snap.kroner));
    this.tierText?.setText(snap.tier);
    this.dagText?.setText(String(snap.dag));
    this.sesongText?.setText(sesongLabel(snap.dag));
    this.omdommeText?.setText(omdommeNivaa(snap.omdomme));
  }

  private formatKroner(n: number): string {
    return `${n.toLocaleString('nb-NO')} kr`;
  }
}

function sesongLabel(dag: number): string {
  const s = sesongForDag(dag);
  return `${SESONG_NAVN[s]} – år ${aaret(dag)}`;
}

export function omdommeNivaa(verdi: number): string {
  if (verdi >= 85) return 'Stjerne';
  if (verdi >= 60) return 'Anerkjent';
  if (verdi >= 30) return 'Etablert';
  return 'Ny';
}
